import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import fonts from '../../design-system/fonts';
import colors from '../../design-system/colors';

export const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${colors.porcelain};
`;

export const Container = styled(motion.div)`
  flex: 1;
  padding: 140px 40px 100px;
  margin: 0 auto;
  width: 100%;
  max-width: 1400px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 120px 20px 60px;
  }
`;

export const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 56px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.deepCharcoal};
  opacity: 0.5;
  font-family: ${fonts.body};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

export const BreadcrumbLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: ${colors.sapphire};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.sapphire};
    opacity: 1;
    &::after {
      width: 100%;
    }
  }
`;

export const BreadcrumbSeparator = styled.span`
  color: ${colors.goldenBronze};
  font-weight: 300;
  font-size: 18px;
`;

export const BreadcrumbCurrent = styled.span`
  font-weight: 700;
  opacity: 1;
  color: ${colors.deepCharcoal};
`;

export const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 100px;
  align-items: center;

  @media (max-width: 1024px) {
    gap: 60px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

export const ImageSection = styled(motion.div)`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
`;

export const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ThumbnailContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
  padding: 0 20px;
`;

export const ThumbnailBox = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid
    ${(props) => (props.$isActive ? colors.sapphire : 'transparent')};
  background: white;
  transition: all 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
  box-shadow: ${(props) =>
    props.$isActive
      ? '0 4px 12px rgba(28, 80, 196, 0.2)'
      : '0 2px 8px rgba(0, 0, 0, 0.1)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    border-color: ${colors.sapphire};
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ProductImage = styled(motion.img)`
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$isAppImage ? 'cover' : 'contain')};
  filter: drop-shadow(0 10px 20px rgba(0, 0, 0, 0.08));
`;

export const InfoSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

export const ProductCategory = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${colors.sapphire};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  margin-bottom: 16px;
  font-family: ${fonts.body};
  display: inline-block;
  padding: 6px 16px;
  background: rgba(28, 80, 196, 0.08);
  border-radius: 100px;
  width: fit-content;
`;

export const ProductTitle = styled.h1`
  font-size: 56px;
  font-weight: 900;
  color: ${colors.deepCharcoal};
  margin: 0 0 24px 0;
  text-transform: uppercase;
  font-family: ${fonts.headline};
  letter-spacing: -0.03em;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 40px;
  }
`;

export const ProductDescription = styled.p`
  font-size: 18px;
  color: ${colors.deepCharcoal};
  opacity: 0.8;
  line-height: 1.8;
  margin-bottom: 48px;
  font-family: ${fonts.body};
  max-width: 90%;
`;

export const DetailGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 56px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

export const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: 8px;
    padding-right: 8px;
    background: rgba(28, 80, 196, 0.02);
  }
`;

export const DetailLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${colors.goldenBronze};
  letter-spacing: 0.12em;
  font-family: ${fonts.body};
`;

export const DetailValue = styled.span`
  font-size: 17px;
  font-weight: 600;
  color: ${colors.deepCharcoal};
  font-family: ${fonts.body};
  text-align: right;
  letter-spacing: -0.01em;
`;

export const EnquiryButton = styled(motion.button)`
  padding: 22px 40px;
  background: ${colors.deepCharcoal};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  cursor: pointer;
  font-family: ${fonts.body};
  transition: all 0.4s cubic-bezier(0.25, 0.1, 0.25, 1);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 5, 0, 0.15);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover {
    background: ${colors.sapphire};
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(28, 80, 196, 0.25);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-2px);
  }
`;
